/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Chip, Alert, CircularProgress, LinearProgress, Grid, Stack,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as eqpToolpathApi from '../../lib/api/eqpToolpath';
import { getEqpStateStyle, getEqpStateLabel } from './eqpState';
import { formatDateTime } from './eqpToolLifeFormat';
import {
  TOOLPATH_STATE, getToolpathStateKind, formatFileNotFoundMessage, INDEXING_MESSAGE, LOCATING_MESSAGE,
  clampPctValue, formatPctValue, formatRemainingMeters, formatProgramLabel, formatFeed, formatSpindleRpm,
  pushEtaSample, computeEtaSec, formatEtaText,
} from './eqpToolpathFormat';

// three.js(+OrbitControls)를 끌고 들어가므로 CadModelViewer(cadquote)와 같은 방식으로 지연 로딩한다 -
// EqpToolpathTab은 장비 상세 모달에서 즉시(lazy 아님) 불러오는 컴포넌트라, 3D 뷰어를 그냥 import하면
// three.js가 메인 번들에 끼어들어가 버린다(실측: +126KB gzip). 3D 탭을 실제로 열 때만 받는다.
const EqpToolpath3DView = lazy(() => import('./EqpToolpath3DView'));

const REFRESH_MS = 10000; // 계획: "10초마다 이미지 새로고침" - progress도 같은 주기로 폴링
const IMAGE_W = 1000;
const IMAGE_H = 667;

// 2D/3D 전환 - 기기별로 달라도 무방한 가벼운 UI 상태라 localStorage에만 둔다(MenuList.js와 같은 패턴).
// 실패해도(프라이빗 모드 등) 화면 동작엔 지장이 없어야 하므로 항상 try/catch.
const VIEW_MODE_KEY = 'ksm.toolpath.viewMode';
const VIEW_MODE_3D = '3D';
const VIEW_MODE_2D = '2D';

const readViewMode = () => {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === VIEW_MODE_3D ? VIEW_MODE_3D : VIEW_MODE_2D;
  } catch {
    return VIEW_MODE_2D;
  }
};

const writeViewMode = (mode) => {
  try {
    localStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    // 프라이빗 모드/용량 초과 등은 무시 - "기억하면 편한" 정도라 치명적이지 않다.
  }
};

// 3D 형상(높이 지도) - 줄이 바뀔 때만, 그리고 최대 30초에 한 번만 다시 받는다(계획: "30초마다
// 갱신(줄이 바뀌었을 때만)"). 2D를 보는 동안은 전혀 요청하지 않는다(탭/useEffect가 viewMode로 분기).
const HEIGHTMAP_MIN_INTERVAL_MS = 30000;
const HEIGHTMAP_N = 256;

const Legend = () => (
  <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
    <LegendItem color="linear-gradient(90deg,#2b6cb0,#38a169,#d69e2e)" label="끝남(깊이=색)" />
    <LegendItem color="#ed8936" label="진행 중(다시 지나갈 자리)" />
    <LegendItem color="#cbd5e0" label="남음" />
    <LegendItem color="#e53e3e" label="현재 위치" marker />
  </Stack>
);

const LegendItem = ({ color, label, marker }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box
      sx={{
        width: 14, height: 14, borderRadius: marker ? '50%' : 0.5,
        background: color, flexShrink: 0,
      }}
    />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

/**
 * (가공 진행) 공구 경로 탭 - 위에서 본 경로(XY, 깊이는 색) 이미지 + 진행률/남은 시간 추정.
 * 계획: 설비모니터링/가공 진행 화면(공구 경로) 계획.md
 * - progress는 10초마다 폴링. 이미지는 line이 바뀔 때만 새로 받는다(캐시 무력화 v=line).
 * - 남은 시간 추정은 순수하게 클라이언트에서: 최근 10분 (시각, remainingLengthMm) 샘플로 속도를 구함
 *   (RUNNING일 때만 샘플을 쌓는다 - 정지 중엔 길이가 안 줄어 속도가 왜곡됨).
 */
const EqpToolpathTab = ({ eqpUuid }) => {
  const theme = useTheme();
  const [progress, setProgress] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [viewMode, setViewMode] = useState(readViewMode);
  const [heightmap, setHeightmap] = useState(null);
  const [heightmapLoading, setHeightmapLoading] = useState(false);
  const etaSamplesRef = useRef([]);
  const lastLineRef = useRef(null);
  const timerRef = useRef(null);
  const objectUrlRef = useRef(null);
  const progressLineRef = useRef(null); // 최신 line - heightmap 폴링 effect가 progress 전체를 의존성에
  // 넣지 않고도(=30초 타이머를 안 흔들고도) 최신 값을 읽게 하려고 ref로 둔다.
  const lastHeightmapLineRef = useRef(null);

  const loadImage = useCallback(async (line) => {
    if (!eqpUuid) return;
    setImageLoading(true);
    try {
      const res = await eqpToolpathApi.getToolpathImageBlob(eqpUuid, { w: IMAGE_W, h: IMAGE_H, v: line });
      const url = URL.createObjectURL(res.data);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setImageUrl(url);
    } catch (e) {
      // 조용히 - 다음 주기에 다시(이미지가 아직 없을 수 있음)
    } finally {
      setImageLoading(false);
    }
  }, [eqpUuid]);

  const loadProgress = useCallback(async () => {
    if (!eqpUuid) return;
    try {
      const res = await eqpToolpathApi.getToolpathProgress(eqpUuid);
      const info = eqpToolpathApi.extractProgress(res);
      setProgress(info);

      if (info && info.runSt === 'RUNNING' && info.remainingLengthMm != null) {
        etaSamplesRef.current = pushEtaSample(etaSamplesRef.current, {
          t: Date.now(),
          remainingMm: info.remainingLengthMm,
        });
      }

      const line = info?.line ?? null;
      if (line !== null && line !== lastLineRef.current) {
        lastLineRef.current = line;
        loadImage(line);
      }
    } catch (e) {
      // 조용히 - 다음 주기에 다시
    }
  }, [eqpUuid, loadImage]);

  useEffect(() => {
    etaSamplesRef.current = [];
    lastLineRef.current = null;
    setHeightmap(null);
    lastHeightmapLineRef.current = null;
    loadProgress();
    timerRef.current = setInterval(loadProgress, REFRESH_MS);
    return () => {
      clearInterval(timerRef.current);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
  }, [eqpUuid]);

  // progress.line 최신값을 ref에도 반영 - heightmap 폴링 effect가 이 ref를 읽는다.
  useEffect(() => {
    progressLineRef.current = progress?.line ?? null;
  }, [progress]);

  const handleViewModeChange = useCallback((_evt, next) => {
    if (!next) return; // ToggleButtonGroup exclusive - 같은 값 다시 누르면 next=null
    setViewMode(next);
    writeViewMode(next);
  }, []);

  const loadHeightmap = useCallback(async () => {
    if (!eqpUuid) return;
    setHeightmapLoading(true);
    try {
      const res = await eqpToolpathApi.getToolpathHeightmap(eqpUuid, HEIGHTMAP_N);
      setHeightmap(eqpToolpathApi.extractHeightmap(res));
    } catch (e) {
      // 조용히 - 다음 주기에 다시(아직 높이 지도가 없을 수 있음)
    } finally {
      setHeightmapLoading(false);
    }
  }, [eqpUuid]);

  // 3D가 보이는 동안만, line이 바뀌었을 때만, 최대 30초에 한 번만 받는다(계획). 2D를 보는 동안은
  // 이 effect 자체가 안 돈다(cleanup으로 타이머가 멈춘 채 유지).
  useEffect(() => {
    if (viewMode !== VIEW_MODE_3D || !eqpUuid) return undefined;

    const tick = () => {
      const line = progressLineRef.current;
      if (line === null || line === lastHeightmapLineRef.current) return;
      lastHeightmapLineRef.current = line;
      loadHeightmap();
    };

    tick(); // 3D로 전환된 직후 바로 한 번
    const timer = setInterval(tick, HEIGHTMAP_MIN_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [viewMode, eqpUuid, loadHeightmap]);

  // 3D가 기억된 상태로 탭을 열면 위 effect의 첫 tick이 progress보다 먼저 돌아(line=null) 30초 동안
  // "데이터 없음"으로 남았다 - 처음 line이 생기는 순간 한 번은 바로 받는다.
  const firstLine = progress?.line ?? null;
  useEffect(() => {
    if (viewMode !== VIEW_MODE_3D || firstLine === null || lastHeightmapLineRef.current !== null) return;
    lastHeightmapLineRef.current = firstLine;
    loadHeightmap();
  }, [viewMode, firstLine, loadHeightmap]);

  const kind = getToolpathStateKind(progress);

  if (kind === TOOLPATH_STATE.LOADING) {
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  }

  if (kind === TOOLPATH_STATE.FILE_NOT_FOUND) {
    return <Alert severity="info">{formatFileNotFoundMessage(progress?.prgNo)}</Alert>;
  }

  if (kind === TOOLPATH_STATE.INDEXING) {
    return (
      <Alert severity="info" icon={<CircularProgress size={18} />}>
        {INDEXING_MESSAGE}
      </Alert>
    );
  }

  if (kind === TOOLPATH_STATE.LOCATING) {
    return <Alert severity="info">{LOCATING_MESSAGE}</Alert>;
  }

  const runStyle = getEqpStateStyle(theme, progress.runSt, false);
  const etaSec = progress.runSt === 'RUNNING' ? computeEtaSec(etaSamplesRef.current) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" component="div">프로그램</Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {formatProgramLabel(progress.prgNo, progress.fileNm)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">공구</Typography>
          <Typography variant="body2">{progress.toolNo != null ? `T${progress.toolNo}` : '-'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">이송</Typography>
          <Typography variant="body2">{formatFeed(progress.feed)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">주축</Typography>
          <Typography variant="body2">{formatSpindleRpm(progress.spindleRpm)}</Typography>
        </Box>
        <Chip
          label={getEqpStateLabel(progress.runSt, false)}
          size="small"
          sx={{ ml: 'auto', bgcolor: runStyle.bg, color: runStyle.main, fontWeight: 700 }}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>진행률 (경로 길이 기준)</Typography>
            <LinearProgress
              variant="determinate"
              value={clampPctValue(progress.pctLength)}
              sx={{ height: 10, borderRadius: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="body2" fontWeight={700}>{formatPctValue(progress.pctLength)}</Typography>
              <Typography variant="caption" color="text.secondary">
                줄 기준 {formatPctValue(progress.pctLine)}
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" component="div">남은 가공 길이</Typography>
              <Typography variant="body2">{formatRemainingMeters(progress.remainingLengthMm)}</Typography>
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" component="div">남은 시간 추정</Typography>
              <Typography variant="body2" fontWeight={600}>
                {progress.runSt === 'RUNNING' ? formatEtaText(etaSec) : '-'}
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" component="div">현재 실행 블록</Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
              >
                {progress.execBlkTxt || '-'}
              </Typography>
              {progress.execBlkDt && (
                <Typography variant="caption" color="text.secondary">
                  읽은 시각 {formatDateTime(progress.execBlkDt)}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
              >
                <ToggleButton value={VIEW_MODE_2D}>2D 경로</ToggleButton>
                <ToggleButton value={VIEW_MODE_3D}>3D 형상</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {viewMode === VIEW_MODE_2D ? (
              <>
                <Box sx={{ position: 'relative', width: '100%', aspectRatio: `${IMAGE_W} / ${IMAGE_H}`, bgcolor: 'grey.100' }}>
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="공구 경로"
                      style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
                    />
                  ) : (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  {imageLoading && imageUrl && (
                    <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
                      <CircularProgress size={14} />
                    </Box>
                  )}
                </Box>
                <Legend />
              </>
            ) : (
              <Suspense
                fallback={(
                  <Box sx={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              >
                <EqpToolpath3DView heightmap={heightmap} loading={heightmapLoading} height={360} />
              </Suspense>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EqpToolpathTab;
